import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw, X } from 'lucide-react';
import {
  ExternalSystemAdmin,
  VerticalLiftStatus,
  VerticalLiftEvent,
  VerticalLiftPendingOrder,
  LIFT_EVENT_TYPES,
  LIFT_FLOWS
} from '../../../../types/externalSystem';
import { externalSystemService } from '../../../../services/externalSystemService';
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils';
import type { ColumnDefinition } from '../../../shared/table/types/table';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface ExternalSystemActivityProps {
  systems: ExternalSystemAdmin[];
}

/** Ordre sendt til heisen uten svar lenger enn dette varsles på statuskortet. */
const SENT_WARNING_HOURS = 4;
/** Ingen vellykket kontakt på så lenge (mens heisen er aktiv) = workeren går trolig ikke. */
const CONTACT_WARNING_MINUTES = 10;
const AUTO_REFRESH_MS = 30_000;

type Period = '24h' | '7d' | '30d' | 'all';

/** Hvilken liste bak statuskortene som er åpen. */
interface PendingView {
  flow: 'Pick' | 'PutAway';
  state: 'Queued' | 'Sent';
}

const periodStart = (period: Period): string | undefined => {
  if (period === 'all') return undefined;
  const hours = period === '24h' ? 24 : period === '7d' ? 24 * 7 : 24 * 30;
  const d = new Date(Date.now() - hours * 3600_000);
  // Lokal tid uten sone — API og database bruker lokal servertid.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
};

const formatDateTime = (iso?: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

const minutesSince = (iso?: string | null): number | null =>
  iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)) : null;

/**
 * Aktivitetsside for vareheis-integrasjonen: statuskort (kontakt, køer, sendt uten svar,
 * tellinger til godkjenning) og hendelseslogg fra VerticalLiftEvent. Oppdateres automatisk.
 */
export const ExternalSystemActivity: React.FC<ExternalSystemActivityProps> = ({ systems }) => {
  const { t } = useTranslation();

  const liftSystems = useMemo(() => systems.filter((s) => !!s.providerKey), [systems]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [status, setStatus] = useState<VerticalLiftStatus | null>(null);
  const [events, setEvents] = useState<VerticalLiftEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const [period, setPeriod] = useState<Period>('7d');
  const [flow, setFlow] = useState('');
  const [eventType, setEventType] = useState('');
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [pendingView, setPendingView] = useState<PendingView | null>(null);
  const [pendingOrders, setPendingOrders] = useState<VerticalLiftPendingOrder[]>([]);
  const [statusError, setStatusError] = useState(false);
  const [pendingError, setPendingError] = useState(false);

  useEffect(() => {
    if (selectedId != null && liftSystems.some((s) => s.id === selectedId)) return;
    const preferred = liftSystems.find((s) => s.activeYesNo) ?? liftSystems[0];
    setSelectedId(preferred ? preferred.id : null);
  }, [liftSystems, selectedId]);

  const selectedSystem = liftSystems.find((s) => s.id === selectedId) ?? null;

  const load = useCallback(async (showSpinner: boolean) => {
    if (selectedId == null) return;
    if (showSpinner) setLoading(true);
    // Delene hentes uavhengig: er ordrelinjene låst, feiler kø-listen, men status og
    // hendelsesloggen (som ikke leser ordrelinjene) vises likevel.
    const [s, e, p] = await Promise.allSettled([
      externalSystemService.getLiftStatus(selectedId),
      externalSystemService.getLiftActivity(selectedId, {
        from: periodStart(period),
        flow: flow || undefined,
        eventType: eventType || undefined,
        onlyErrors,
        take: 1000
      }),
      externalSystemService.getLiftPendingOrders(selectedId)
    ]);

    if (s.status === 'fulfilled') setStatus(s.value);
    if (e.status === 'fulfilled') setEvents(e.value);
    if (p.status === 'fulfilled') setPendingOrders(p.value);
    setStatusError(s.status === 'rejected');
    setPendingError(p.status === 'rejected');
    setError(e.status === 'rejected'
      ? t('manager.modules.externalSystems.activity.loadFailed', {}, 'Feil ved lasting av heisaktivitet')
      : null);
    [s, e, p].forEach((r) => { if (r.status === 'rejected') console.error('Error loading lift activity:', r.reason); });
    setLastRefreshed(new Date());
    if (showSpinner) setLoading(false);
  }, [selectedId, period, flow, eventType, onlyErrors, t]);

  useEffect(() => {
    load(true);
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const handle = window.setInterval(() => load(false), AUTO_REFRESH_MS);
    return () => window.clearInterval(handle);
  }, [autoRefresh, load]);

  const eventTypeLabel = useCallback((type: string): string => {
    const fallback: Record<string, string> = {
      Sent: 'Sendt til heis',
      SendFailed: 'Sending feilet',
      Confirmed: 'Bekreftet av heis',
      Finalized: 'Fullført i Compass',
      CountRegistered: 'Telling registrert',
      Skipped: 'Hoppet over',
      ApplyFailed: 'Behandling feilet',
      ContactFailed: 'Mistet kontakt',
      ContactRestored: 'Kontakt gjenopprettet',
      ProcessingFailed: 'Integrasjonen feiler',
      ProcessingRestored: 'Integrasjonen virker igjen'
    };
    return t(`manager.modules.externalSystems.activity.eventTypes.${type}`, {}, fallback[type] ?? type);
  }, [t]);

  const flowLabel = useCallback((value?: string | null): string => {
    if (!value) return t('manager.modules.externalSystems.activity.flows.lift', {}, 'Heis');
    const fallback: Record<string, string> = { Pick: 'Plukk', PutAway: 'Utlegg', StockCount: 'Telling', Confirmations: 'Bekreftelser', Other: 'Annet' };
    return t(`manager.modules.externalSystems.activity.flows.${value}`, {}, fallback[value] ?? value);
  }, [t]);

  const formatAge = useCallback((minutes: number): string => {
    if (minutes < 60) return t('manager.modules.externalSystems.activity.ageMinutes', { '0': minutes }, `${minutes} min`);
    if (minutes < 60 * 48) return t('manager.modules.externalSystems.activity.ageHours', { '0': Math.floor(minutes / 60) }, `${Math.floor(minutes / 60)} t`);
    return t('manager.modules.externalSystems.activity.ageDays', { '0': Math.floor(minutes / 1440) }, `${Math.floor(minutes / 1440)} d`);
  }, [t]);

  const columnFactory = createColumnFactory<VerticalLiftEvent>();

  const columns: ColumnDefinition<VerticalLiftEvent>[] = useMemo(() => [
    columnFactory.createTextColumn('eventTime', 'eventTime', t('manager.modules.externalSystems.activity.columns.time', {}, 'Tidspunkt'), {
      width: 160, sortable: true,
      formatter: (value: any) => formatDateTime(value)
    }),
    columnFactory.createTextColumn('eventType', 'eventType', t('manager.modules.externalSystems.activity.columns.event', {}, 'Hendelse'), {
      width: 170, sortable: true,
      formatter: (value: any) => eventTypeLabel(String(value ?? '')),
      renderer: (value: any, row: VerticalLiftEvent) => (
        <span className={row.isError ? 'text-red-700 font-medium' : ''}>{eventTypeLabel(String(value ?? ''))}</span>
      )
    }),
    columnFactory.createTextColumn('flow', 'flow', t('manager.modules.externalSystems.activity.columns.flow', {}, 'Flyt'), {
      width: 100, sortable: true,
      formatter: (value: any) => flowLabel(value)
    }),
    columnFactory.createTextColumn('orderNumber', 'orderNumber', t('manager.modules.externalSystems.activity.columns.order', {}, 'Ordre'), {
      width: 120, sortable: true, filterType: 'text'
    }),
    columnFactory.createTextColumn('description', 'description', t('manager.modules.externalSystems.activity.columns.description', {}, 'Beskrivelse'), {
      width: 280, sortable: true, filterType: 'text'
    }),
    columnFactory.createNumberColumn('quantity', 'quantity', t('manager.modules.externalSystems.activity.columns.quantity', {}, 'Antall'), {
      width: 90,
      formatter: (value: any) => (value == null ? '' : String(Number(value)))
    }),
    columnFactory.createTextColumn('message', 'message', t('manager.modules.externalSystems.activity.columns.message', {}, 'Melding'), {
      width: 380, filterType: 'text'
    }),
    columnFactory.createNumberColumn('documentID', 'documentID', t('manager.modules.externalSystems.activity.columns.document', {}, 'Heisdokument'), {
      width: 110,
      formatter: (value: any) => (value == null ? '' : String(value))
    })
  ], [t, eventTypeLabel, flowLabel]);

  if (liftSystems.length === 0) {
    return (
      <div className="p-6 text-sm text-gray-600">
        {t('manager.modules.externalSystems.activity.noLifts', {}, 'Ingen heiser med provider er satt opp ennå. Opprett en under «Oppsett».')}
      </div>
    );
  }

  // Avledet status for kortene
  const contactFailing = !!status?.lastErrorAt && (!status.lastContactAt || status.lastErrorAt > status.lastContactAt);
  const contactAge = minutesSince(status?.lastContactAt);
  const contactStale = !!selectedSystem?.activeYesNo && !contactFailing && (contactAge == null || contactAge > CONTACT_WARNING_MINUTES);
  const pickSentAge = status?.pickSent ? minutesSince(status.oldestPickSentAt) : null;
  const putAwaySentAge = status?.putAwaySent ? minutesSince(status.oldestPutAwaySentAt) : null;
  const pickSentWarning = pickSentAge != null && pickSentAge > SENT_WARNING_HOURS * 60;
  const putAwaySentWarning = putAwaySentAge != null && putAwaySentAge > SENT_WARNING_HOURS * 60;

  const contactDot = !selectedSystem?.activeYesNo ? 'bg-gray-300' : contactFailing ? 'bg-red-500' : contactStale ? 'bg-yellow-400' : 'bg-green-500';
  const contactText = !selectedSystem?.activeYesNo
    ? t('manager.modules.externalSystems.activity.inactive', {}, 'Ikke aktiv')
    : contactFailing
      ? t('manager.modules.externalSystems.activity.contactFailing', {}, 'Ingen kontakt')
      : contactStale
        ? t('manager.modules.externalSystems.activity.contactStale', {}, 'Ingen kontakt nylig')
        : t('manager.modules.externalSystems.activity.contactOk', {}, 'Kontakt OK');

  const cardClass = 'bg-white border border-gray-200 rounded-lg p-4';
  const labelClass = 'text-xs font-medium text-gray-500 uppercase tracking-wide';
  const selectClass = 'px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  const togglePending = (view: PendingView) =>
    setPendingView((prev) => (prev && prev.flow === view.flow && prev.state === view.state ? null : view));

  const isOpen = (view: PendingView) => pendingView?.flow === view.flow && pendingView?.state === view.state;

  // Klikkbar linje på kortet — åpner/lukker listen over ordrene bak tallet.
  const pendingToggle = (view: PendingView, count: number, content: React.ReactNode, className: string) => (
    <button type="button" onClick={() => togglePending(view)} disabled={count === 0}
      className={`flex items-center gap-1 text-left hover:underline disabled:no-underline disabled:cursor-default ${className}`}>
      {count > 0 && (isOpen(view) ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
      {content}
    </button>
  );

  const queuedLine = (flowKey: PendingView['flow'], count: number) =>
    pendingToggle({ flow: flowKey, state: 'Queued' }, count,
      t('manager.modules.externalSystems.activity.queued', { '0': count }, `${count} i kø`),
      'text-base font-semibold text-gray-900 mt-2');

  const sentLine = (flowKey: PendingView['flow'], count: number, age: number | null, warning: boolean) =>
    pendingToggle({ flow: flowKey, state: 'Sent' }, count, (
      <>
        {warning && <AlertTriangle size={14} />}
        {t('manager.modules.externalSystems.activity.sent', { '0': count }, `${count} sendt — venter på heisen`)}
        {age != null && (
          <span className="text-gray-500 font-normal">
            {' · '}{t('manager.modules.externalSystems.activity.oldest', { '0': formatAge(age) }, `eldste ${formatAge(age)}`)}
          </span>
        )}
      </>
    ), `text-sm ${warning ? 'text-red-700 font-medium' : 'text-gray-700'}`);

  const visiblePending = pendingView
    ? pendingOrders.filter((o) => o.flow === pendingView.flow && o.state === pendingView.state)
    : [];

  const queueUnavailableText = (
    <div className="text-sm text-yellow-800 mt-2 flex items-center gap-1">
      <AlertTriangle size={14} />
      {t('manager.modules.externalSystems.activity.queueUnavailable', {}, 'Køen kunne ikke leses — ordrelinjene er låst')}
    </div>
  );

  const failureFlowText = (f: string): string => {
    const fallback: Record<string, string> = {
      Pick: 'Sending av plukk', PutAway: 'Sending av utlegg', Confirmations: 'Henting av bekreftelser'
    };
    return t(`manager.modules.externalSystems.activity.failureFlows.${f}`, {}, fallback[f] ?? f);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Velg heis + oppdatering */}
      <div className="flex flex-wrap items-center gap-3 px-4 pt-3">
        {liftSystems.length > 1 && (
          <select value={selectedId ?? ''} onChange={(e) => setSelectedId(parseInt(e.target.value, 10))} className={selectClass}
            aria-label={t('manager.modules.externalSystems.activity.selectLift', {}, 'Velg heis')}>
            {liftSystems.map((s) => (
              <option key={s.id} value={s.id}>
                {s.externalCustomID || s.externalSystemID}{s.description1 ? ` — ${s.description1}` : ''}
              </option>
            ))}
          </select>
        )}
        {liftSystems.length === 1 && selectedSystem && (
          <span className="text-sm font-medium text-gray-900">
            {selectedSystem.externalCustomID || selectedSystem.externalSystemID}{selectedSystem.description1 ? ` — ${selectedSystem.description1}` : ''}
          </span>
        )}
        <div className="flex items-center gap-3 ml-auto text-sm text-gray-600">
          {lastRefreshed && (
            <span>{t('manager.modules.externalSystems.activity.updatedAt', { '0': lastRefreshed.toLocaleTimeString('nb-NO') }, `Oppdatert ${lastRefreshed.toLocaleTimeString('nb-NO')}`)}</span>
          )}
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            {t('manager.modules.externalSystems.activity.autoRefresh', {}, 'Auto-oppdater')}
          </label>
          <button onClick={() => load(true)} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {t('common.buttons.refresh', {}, 'Oppdater')}
          </button>
        </div>
      </div>

      {/* Flyter i workeren som feiler nå */}
      {status?.openFailures?.map((f) => (
        <div key={f.flow} className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>
            <span className="font-medium">
              {t('manager.modules.externalSystems.activity.failureTitle', { '0': failureFlowText(f.flow), '1': formatDateTime(f.since) },
                `${failureFlowText(f.flow)} feiler siden ${formatDateTime(f.since)}.`)}
            </span>
            {/* Meldingen i loggen er «<flyt> feiler: <årsak>» — banneret har flyten i tittelen, så vis bare årsaken */}
            {' '}{(f.message ?? '').replace(/^[^:]*:\s*/, '').replace(/\.?$/, '.')}{' '}
            {t('manager.modules.externalSystems.activity.failureHint', {}, 'Det som venter, behandles automatisk når feilen er borte.')}
          </span>
        </div>
      ))}
      {statusError && (
        <div className="mx-4 mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          {t('manager.modules.externalSystems.activity.statusUnavailable', {}, 'Statusen kunne ikke hentes. Viser sist kjente verdier.')}
        </div>
      )}

      {/* Statuskort */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 px-4 pt-3">
        <div className={cardClass}>
          <div className={labelClass}>{t('manager.modules.externalSystems.activity.cardContact', {}, 'Tilkobling')}</div>
          <div className="flex items-center gap-2 mt-2">
            <span className={`inline-block w-3 h-3 rounded-full ${contactDot}`} />
            <span className="text-base font-semibold text-gray-900">{contactText}</span>
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {t('manager.modules.externalSystems.activity.lastContact', {}, 'Sist kontakt')}: {formatDateTime(status?.lastContactAt)}
            {contactAge != null && <span className="text-gray-500"> ({formatAge(contactAge)})</span>}
          </div>
          {contactFailing && status?.lastError && (
            <div className="text-sm text-red-700 mt-1 break-words">{status.lastError}</div>
          )}
        </div>

        <div className={cardClass}>
          <div className={labelClass}>{t('manager.modules.externalSystems.activity.cardPick', {}, 'Plukk')}</div>
          {status?.queueUnavailable ? queueUnavailableText : (
            <>
              {queuedLine('Pick', status?.pickQueued ?? 0)}
              {sentLine('Pick', status?.pickSent ?? 0, pickSentAge, pickSentWarning)}
            </>
          )}
        </div>

        <div className={cardClass}>
          <div className={labelClass}>{t('manager.modules.externalSystems.activity.cardPutAway', {}, 'Utlegg')}</div>
          {status?.queueUnavailable ? queueUnavailableText : (
            <>
              {queuedLine('PutAway', status?.putAwayQueued ?? 0)}
              {sentLine('PutAway', status?.putAwaySent ?? 0, putAwaySentAge, putAwaySentWarning)}
            </>
          )}
        </div>

        <div className={cardClass}>
          <div className={labelClass}>{t('manager.modules.externalSystems.activity.cardCount', {}, 'Telling')}</div>
          <div className={`text-base font-semibold mt-2 ${status?.countsAwaitingApproval ? 'text-yellow-800' : 'text-gray-900'}`}>
            {t('manager.modules.externalSystems.activity.awaitingApproval', { '0': status?.countsAwaitingApproval ?? 0 }, `${status?.countsAwaitingApproval ?? 0} venter på godkjenning`)}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {t('manager.modules.externalSystems.activity.lastEvent', {}, 'Siste hendelse')}: {formatDateTime(status?.lastEventAt)}
          </div>
        </div>
      </div>

      {/* Ordrene bak tallet på kortet */}
      {pendingView && (
        <div className="mx-4 mt-3 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
            <span className="text-sm font-semibold text-gray-900">
              {flowLabel(pendingView.flow)} — {pendingView.state === 'Sent'
                ? t('manager.modules.externalSystems.activity.pendingSentTitle', {}, 'sendt til heisen, venter på svar')
                : t('manager.modules.externalSystems.activity.pendingQueuedTitle', {}, 'i kø til heisen')}
            </span>
            <button type="button" onClick={() => setPendingView(null)} className="text-gray-400 hover:text-gray-600"
              aria-label={t('common.buttons.close', {}, 'Lukk')}>
              <X size={16} />
            </button>
          </div>
          {pendingError ? (
            <div className="px-4 py-3 text-sm text-yellow-800">
              {t('manager.modules.externalSystems.activity.queueUnavailable', {}, 'Køen kunne ikke leses — ordrelinjene er låst')}
            </div>
          ) : visiblePending.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-600">
              {t('manager.modules.externalSystems.activity.pendingEmpty', {}, 'Ingen ordrer.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-2 font-medium">{t('manager.modules.externalSystems.activity.columns.order', {}, 'Ordre')}</th>
                    <th className="px-4 py-2 font-medium">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                    <th className="px-4 py-2 font-medium">
                      {pendingView.state === 'Sent'
                        ? t('manager.modules.externalSystems.activity.sentAt', {}, 'Sendt')
                        : t('manager.modules.externalSystems.activity.createdAt', {}, 'Opprettet')}
                    </th>
                    <th className="px-4 py-2 font-medium">{t('manager.modules.externalSystems.activity.waited', {}, 'Ventet')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('manager.modules.externalSystems.activity.lines', {}, 'Linjer')}</th>
                    <th className="px-4 py-2 font-medium text-right">{t('manager.modules.externalSystems.activity.columns.quantity', {}, 'Antall')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePending.map((o) => {
                    const age = minutesSince(o.since);
                    const late = pendingView.state === 'Sent' && age != null && age > SENT_WARNING_HOURS * 60;
                    return (
                      <tr key={`${o.flow}-${o.orderNumber}`} className="border-t border-gray-200">
                        <td className="px-4 py-2 font-medium text-gray-900">{o.orderNumber}</td>
                        <td className="px-4 py-2 text-gray-700">{o.description1 || ''}</td>
                        <td className="px-4 py-2 text-gray-700">{formatDateTime(o.since)}</td>
                        <td className={`px-4 py-2 ${late ? 'text-red-700 font-medium' : 'text-gray-700'}`}>
                          <span className="flex items-center gap-1">
                            {late && <AlertTriangle size={14} />}
                            {age != null ? formatAge(age) : '—'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700">{o.lines}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{o.quantity != null ? Number(o.quantity) : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {(pickSentWarning || putAwaySentWarning) && (
        <div className="mx-4 mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>
            {t('manager.modules.externalSystems.activity.sentWarning', { '0': SENT_WARNING_HOURS },
              `Noe har vært sendt til heisen i mer enn ${SENT_WARNING_HOURS} timer uten svar. Sjekk på heisens skjerm om ordren er utført eller kansellert der.`)}
          </span>
        </div>
      )}

      {/* Hendelseslogg */}
      <div className="flex-1 min-h-0 mt-3">
        <WarehouseTable
          data={events}
          columns={columns}
          loading={loading && events.length === 0}
          error={error || undefined}
          config={{
            persistenceKey: 'externalsystem-activity-table',
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 100,
            compactMode: true,
            stickyHeader: true,
            allowColumnReorder: true,
            allowColumnResize: true
          }}
          getRowClassName={(row) => (row.isError ? 'bg-red-50' : undefined)}
          toolbarLeft={
            <div className="flex flex-wrap items-center gap-2">
              <select value={period} onChange={(e) => setPeriod(e.target.value as Period)} className={selectClass}
                aria-label={t('manager.modules.externalSystems.activity.period', {}, 'Periode')}>
                <option value="24h">{t('manager.modules.externalSystems.activity.period24h', {}, 'Siste døgn')}</option>
                <option value="7d">{t('manager.modules.externalSystems.activity.period7d', {}, 'Siste 7 dager')}</option>
                <option value="30d">{t('manager.modules.externalSystems.activity.period30d', {}, 'Siste 30 dager')}</option>
                <option value="all">{t('manager.modules.externalSystems.activity.periodAll', {}, 'Alle')}</option>
              </select>
              <select value={flow} onChange={(e) => setFlow(e.target.value)} className={selectClass}
                aria-label={t('manager.modules.externalSystems.activity.columns.flow', {}, 'Flyt')}>
                <option value="">{t('manager.modules.externalSystems.activity.allFlows', {}, 'Alle flyter')}</option>
                {LIFT_FLOWS.map((f) => <option key={f} value={f}>{flowLabel(f)}</option>)}
              </select>
              <select value={eventType} onChange={(e) => setEventType(e.target.value)} className={selectClass}
                aria-label={t('manager.modules.externalSystems.activity.columns.event', {}, 'Hendelse')}>
                <option value="">{t('manager.modules.externalSystems.activity.allEvents', {}, 'Alle hendelser')}</option>
                {LIFT_EVENT_TYPES.map((et) => <option key={et} value={et}>{eventTypeLabel(et)}</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-sm text-gray-700">
                <input type="checkbox" checked={onlyErrors} onChange={(e) => setOnlyErrors(e.target.checked)} />
                {t('manager.modules.externalSystems.activity.onlyErrors', {}, 'Bare feil')}
              </label>
            </div>
          }
          aria-label={t('manager.modules.externalSystems.activity.tableAriaLabel', {}, 'Hendelseslogg for vareheis')}
        />
      </div>
    </div>
  );
};
